import { useState } from 'react';
import Breadcrumb from '../../components/Breadcrumb';
import { useToast } from '../../components/Toast';
import { migrateProjectTypedTokens, TokenMigrationStats } from '../../api/adminProjects';

const MAX_DANGLING_PREVIEW = 20;

export default function TokenMigration() {
  const toast = useToast();
  const [projectId, setProjectId] = useState('');
  const [loading, setLoading] = useState(false);
  const [dryStats, setDryStats] = useState<TokenMigrationStats | null>(null);
  const [doneStats, setDoneStats] = useState<TokenMigrationStats | null>(null);

  const run = async (confirmed: boolean) => {
    setLoading(true);
    if (!confirmed) setDoneStats(null);
    try {
      const stats = await migrateProjectTypedTokens(projectId.trim(), confirmed);
      if (confirmed) {
        setDoneStats(stats);
        toast.success(`迁移完成，已更新 ${stats.migratedCount} 个分镜`);
      } else {
        setDryStats(stats);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '请求失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem' }}>
      <Breadcrumb items={[{ label: '首页', to: '/projects' }, { label: '匹配迁移' }]} />
      <div className="page-header">
        <h1 className="page-title">匹配迁移</h1>
      </div>

      <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
        <p style={{ color: 'var(--text-2)', marginTop: 0 }}>
          按项目把存量分镜提示词里的旧格式引用（如 <code>@1 @2</code>）转换成类型化引用（<code>@图片N</code>／<code>@视频N</code>／<code>@音频N</code>）。先预检（不写库），确认后再迁移；可重复执行。
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="input"
            placeholder="项目 ID"
            aria-label="项目 ID"
            value={projectId}
            onChange={(e) => { setProjectId(e.target.value); setDryStats(null); setDoneStats(null); }}
            style={{ flex: 1, minWidth: 260 }}
          />
          <button className="btn btn-ghost" onClick={() => run(false)} disabled={loading || !projectId.trim()}>预检</button>
          <button className="btn btn-primary" onClick={() => run(true)} disabled={loading || !dryStats}>确认迁移</button>
        </div>
      </div>

      {dryStats && !doneStats && (
        <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
          <h2 className="page-title" style={{ fontSize: '1.05rem', marginTop: 0 }}>预检结果</h2>
          <div>总分镜 {dryStats.totalShots}，含旧格式 {dryStats.shotsWithLegacy}，会变更 <b>{dryStats.shotsWouldChange}</b></div>
          <div style={{ color: 'var(--text-2)', marginTop: '0.25rem' }}>
            无法转换 {dryStats.danglingCount}，处理失败 {dryStats.failedCount}
            {dryStats.truncated ? '（明细过多已截断，完整记录见后端日志）' : ''}
          </div>
          {dryStats.errors.length > 0 && (
            <ul style={{ marginTop: '0.5rem' }}>
              {dryStats.errors.map((e) => <li key={e.shotId}>{e.shotId}：{e.error}</li>)}
            </ul>
          )}
          {dryStats.sample && dryStats.sample.length > 0 && (
            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>转换样例</div>
              {dryStats.sample.map((s) => (
                <div key={s.shotId} style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--text-2)' }}>
                  {s.before} → {s.after}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {doneStats && (
        <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
          <h2 className="page-title" style={{ fontSize: '1.05rem', marginTop: 0 }}>迁移完成</h2>
          <div>已迁移 <b>{doneStats.migratedCount}</b>，处理失败 {doneStats.failedCount}，无法转换 {doneStats.danglingCount}
            {doneStats.truncated ? '（明细过多已截断，完整记录见后端日志）' : ''}
          </div>
          {doneStats.danglingShotIds.length > 0 && (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-2)', marginTop: '0.25rem' }}>
              无法转换的分镜（前 {Math.min(doneStats.danglingShotIds.length, MAX_DANGLING_PREVIEW)} 条）：{doneStats.danglingShotIds.slice(0, MAX_DANGLING_PREVIEW).join('、')}
            </div>
          )}
          {doneStats.errors.length > 0 && (
            <ul style={{ marginTop: '0.5rem' }}>
              {doneStats.errors.map((e) => <li key={e.shotId}>{e.shotId}：{e.error}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
